import { computeShiftCost } from './shiftCost';

function toYMD(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Pure function — no side effects, no API calls.
 * @param {Array} shifts  ShiftResponse[] from API
 * @param {Array} employees  EmployeeResponse[] from API
 * @param {Array} weekDays  Date[] (7 items, Mon–Sun)
 * @param {Map} [leaveMap]  Story 15b Item 2 — Map<"employeeId-YYYY-MM-DD", leaveEntry>, same
 *   keying as Scheduler.jsx's leaveMap. A shift whose (employee, day) is in this map is a
 *   needs-cover conflict (approved leave landed on a day that already had a shift) — it isn't
 *   truly staffed, so its hours/cost are excluded from totals, daily totals, and the cap check.
 */
export function computeTotals(shifts, employees, weekDays, leaveMap) {
  const empMap = new Map((employees ?? []).map((e) => [e.id, e]));

  const perDay = new Map();
  for (const d of weekDays ?? []) {
    perDay.set(toYMD(d), { minutes: 0, costSmallestUnit: 0 });
  }

  const perEmployeeMinutes = new Map();
  let totalMinutes = 0;
  let totalCostSmallestUnit = 0;
  let offCellCount = 0;
  let shiftCount = 0;

  for (const shift of shifts ?? []) {
    if (shift.isOff) {
      offCellCount++;
      continue;
    }

    shiftCount++;

    // Needs-cover conflict: the cell still counts as scheduled (not empty), but its hours/cost
    // aren't counted as staffed coverage.
    const onLeaveConflict = leaveMap ? leaveMap.has(`${shift.employeeId}-${shift.shiftDate}`) : false;
    if (onLeaveConflict) continue;

    const emp = empMap.get(shift.employeeId);
    const { minutes: mins, costSmallestUnit: cost } = computeShiftCost(shift, emp);

    totalMinutes += mins;
    totalCostSmallestUnit += cost;

    const dayEntry = perDay.get(shift.shiftDate);
    if (dayEntry) {
      dayEntry.minutes += mins;
      dayEntry.costSmallestUnit += cost;
    }

    perEmployeeMinutes.set(
      shift.employeeId,
      (perEmployeeMinutes.get(shift.employeeId) ?? 0) + mins
    );
  }

  const emptyCellCount =
    (employees ?? []).length * (weekDays ?? []).length - shiftCount - offCellCount;

  const capWarningEmployeeIds = new Set();
  for (const emp of employees ?? []) {
    const capMinutes = (emp.weeklyHourCap ?? 48) * 60;
    const scheduled = perEmployeeMinutes.get(emp.id) ?? 0;
    if (scheduled > capMinutes) {
      capWarningEmployeeIds.add(emp.id);
    }
  }

  return {
    totalMinutes,
    totalCostSmallestUnit,
    perDay,
    emptyCellCount: Math.max(0, emptyCellCount),
    offCellCount,
    perEmployeeMinutes,
    capWarningEmployeeIds,
  };
}